/**
 * MOXIE: escapa texto para interpolación segura en HTML.
 * Los items vienen de localStorage (título/imagen/url del catálogo); sin escape
 * hay vector de XSS almacenado vía datos de producto.
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.updateState();

    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });

    window.addEventListener('wishlist:updated', () => this.updateState());
  }

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem('mox_wishlist') || '[]');
    } catch {
      return [];
    }
  }

  isInWishlist() {
    return this.getWishlist().some((item) => item.id === this.productId);
  }

  toggle() {
    const wishlist = this.getWishlist();
    const idx = wishlist.findIndex((item) => item.id === this.productId);
    if (idx > -1) {
      wishlist.splice(idx, 1);
    } else {
      wishlist.push({
        id: this.productId,
        handle: this.dataset.handle,
        title: this.dataset.title,
        price: this.dataset.price,
        image: this.dataset.image,
        url: this.dataset.url,
      });
    }
    try {
      localStorage.setItem('mox_wishlist', JSON.stringify(wishlist));
    } catch {
      // Storage bloqueado o lleno (p. ej. navegador embebido de Instagram/Facebook):
      // no truena la interacción, solo no persiste entre sesiones.
    }
    window.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  updateState() {
    const active = this.isInWishlist();
    this.button.setAttribute('aria-pressed', String(active));
    this.classList.toggle('wishlist-button--active', active);
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.update();
    window.addEventListener('wishlist:updated', () => this.update());
  }

  getCount() {
    try {
      return JSON.parse(localStorage.getItem('mox_wishlist') || '[]').length;
    } catch {
      return 0;
    }
  }

  update() {
    const count = this.getCount();
    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;
  }
}

class WishlistPage extends HTMLElement {
  connectedCallback() {
    // Defer render so children are guaranteed to be in the DOM
    // (handles both initial load and dynamic section re-injection by Shopify editor)
    requestAnimationFrame(() => this.render());
    window.addEventListener('wishlist:updated', () => this.render());
  }

  getWishlist() {
    try {
      return JSON.parse(localStorage.getItem('mox_wishlist') || '[]');
    } catch {
      return [];
    }
  }

  removeItem(id) {
    const updated = this.getWishlist().filter((item) => item.id !== id);
    try {
      localStorage.setItem('mox_wishlist', JSON.stringify(updated));
    } catch {
      // Storage bloqueado o lleno (p. ej. navegador embebido de Instagram/Facebook):
      // no truena la interacción, solo no persiste entre sesiones.
    }
    window.dispatchEvent(new CustomEvent('wishlist:updated'));
  }

  formatPrice(cents) {
    const value = parseInt(cents, 10) / 100;
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  render() {
    const wishlist = this.getWishlist();
    const emptyEl = this.querySelector('.wishlist-page__empty');
    const gridEl = this.querySelector('.wishlist-page__grid');

    if (!gridEl) return;

    if (wishlist.length === 0) {
      if (emptyEl) emptyEl.hidden = false;
      gridEl.innerHTML = '';
      return;
    }

    if (emptyEl) emptyEl.hidden = true;

    gridEl.innerHTML = wishlist
      .map(
        (item) => `
      <div class="wishlist-page__item">
        <div class="wishlist-page__item-image">
          <a href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title)}" loading="lazy">` : ''}
          </a>
        </div>
        <div class="wishlist-page__item-info">
          <a href="${escapeHtml(item.url)}" class="wishlist-page__item-title">${escapeHtml(item.title)}</a>
          <span class="wishlist-page__item-price">${this.formatPrice(item.price)}</span>
        </div>
        <button
          class="wishlist-page__remove button-unstyled"
          data-product-id="${escapeHtml(item.id)}"
          aria-label="Eliminar de favoritos"
        >&times;</button>
      </div>
    `
      )
      .join('');

    gridEl.querySelectorAll('.wishlist-page__remove').forEach((btn) => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.productId));
    });
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
